import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

// Hooks & Contexts
import { useNavigationHelpers } from "@/lib/navigation-helpers";
import { useAuth } from "@/lib/auth-context";
import { usePermissions } from "@/hooks/use-permissions";
import { useTarefaDetail } from "@/hooks/tarefas/use-tarefa-detail";

// Utils & Tipos
import { TarefaTipo } from "@workflow/types";
import { isSamePessoa } from "@/lib/utils";

// Sub-componentes da Feature
import { TarefaHeader } from "./components/TarefaHeader";
import { TarefaOrigemBanner } from "./components/TarefaOrigemBanner";
import { TarefaSidebarInfo } from "./components/TarefaSidebarInfo";
import { TarefaConteudoCard } from "./components/TarefaConteudoCard";
import { TarefaDetailSkeleton } from "./components/TarefaDetailSkeleton";
import { TarefaChatCard } from "@/components/tarefa/TarefaChatCard";
import { TarefaArquivosCard } from "@/components/tarefa/TarefaArquivosCard";
import { TarefaObservadoresCard } from "./components/TarefaObservadoresCard";
import { ConclusaoRenderer } from "@/components/tarefa/conclusao/ConclusaoRenderer";
import { TarefaHistoricoDialog } from "@/components/tarefa/TarefaHistoricoDialog";
import { EmptyState } from "@/components/ui/estado-vazio";

export default function TarefaDetailPage() {
  const { id } = useParams<{ id: string }>();
  const tarefaId = Number(id);

  const [isHistoricoOpen, setHistoricoOpen] = useState(false);

  const { user } = useAuth();
  const { isAdmin } = usePermissions();
  const { goToJobDetails, goToTarefaList } = useNavigationHelpers();

  const {
    tarefa,
    isLoading,
    isError,
    isActionLoading,
    isUpdateDataPrazoLoading,
    isEditarGenericaLoading,
    statusMenu,
    handleAction,
    handleUpdateDataPrazo,
    handleEditarGenerica,
  } = useTarefaDetail(tarefaId);

  // Título da aba reflete a tarefa aberta (restaura ao sair).
  useEffect(() => {
    if (!tarefa?.nome) return;
    const anterior = document.title;
    document.title = `${tarefa.nome} · Tarefa`;
    return () => {
      document.title = anterior;
    };
  }, [tarefa?.nome]);

  if (isLoading) return <TarefaDetailSkeleton />;

  if (isError || !tarefa) {
    return (
      <EmptyState
        className="py-24"
        message={
          isError
            ? "Não foi possível carregar a tarefa. Ela pode não existir ou você não ter acesso a ela."
            : "Tarefa não encontrada."
        }
        actionLabel="Voltar para tarefas"
        onAction={() => goToTarefaList()}
      />
    );
  }

  const permissions = {
    isAdmin,
    isCriador: isSamePessoa(user, tarefa.criadoPor),
    isResponsavel: isSamePessoa(user, tarefa.atribuidoA),
  };

  const canManageTask = permissions.isAdmin || permissions.isCriador || permissions.isResponsavel;
  const isFechada = statusMenu?.isFechada ?? false;
  const showConclusao = !isFechada && canManageTask;

  // Edição de conteúdo: só tarefa genérica, aberta e pelo criador (ou admin).
  const canEditGenerica =
    tarefa.tipo === TarefaTipo.GENERICA &&
    !isFechada &&
    (permissions.isCriador || permissions.isAdmin);

  return (
    <div className="mx-auto space-y-6 pb-12 duration-500 animate-in fade-in">
      <TarefaHeader
        tarefa={tarefa}
        isActionLoading={isActionLoading}
        canManageStatus={canManageTask}
        onAction={handleAction}
        onBack={() => goToTarefaList()}
        onOpenHistorico={() => setHistoricoOpen(true)}
      />

      <TarefaHistoricoDialog
        open={isHistoricoOpen}
        onOpenChange={setHistoricoOpen}
        tarefaId={tarefa.id}
        tarefaNome={tarefa.nome}
      />

      <TarefaOrigemBanner tarefa={tarefa} />

      {/*
        No mobile (1 coluna) as colunas viram `display:contents`, então os blocos
        são reordenados via `order-*` para a ação de conclusão e as infos virem
        antes do chat. No `lg` voltam a ser duas colunas reais e a ordem do DOM vale.
      */}
      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-12">
        <div className="contents min-w-0 lg:col-span-8 lg:flex lg:flex-col lg:gap-6">
          <div className="order-2 min-w-0 lg:order-none">
            <TarefaConteudoCard tarefa={tarefa} />
          </div>

          <div className="order-6 min-w-0 lg:order-none">
            <TarefaChatCard
              tarefaId={tarefa.id}
              isConcluido={isFechada}
              criadoPorId={tarefa.criadoPor?.id}
              atribuidoAId={tarefa.atribuidoA?.id}
              observadoresIds={tarefa.tarefaObservadores?.map((obs) => obs.pessoa.id)}
            />
          </div>
        </div>

        <aside className="contents min-w-0 lg:col-span-4 lg:flex lg:flex-col lg:gap-6">
          {showConclusao && (
            <div className="order-1 min-w-0 lg:order-none">
              <ConclusaoRenderer tarefa={tarefa} />
            </div>
          )}

          <div className="order-3 min-w-0 lg:order-none">
            <TarefaSidebarInfo
              tarefa={tarefa}
              permissions={permissions}
              isFechada={isFechada}
              isUpdateLoading={isUpdateDataPrazoLoading}
              canEditVisibilidade={canEditGenerica}
              isUpdateVisibilidadeLoading={isEditarGenericaLoading}
              onNavigateToJob={goToJobDetails}
              onUpdateDataPrazo={handleUpdateDataPrazo}
              onUpdateVisibilidade={(visibilidade) => handleEditarGenerica({ visibilidade })}
            />
          </div>

          <div className="order-4 min-w-0 lg:order-none">
            <TarefaObservadoresCard
              tarefaId={tarefa.id}
              isConcluido={isFechada}
              canManage={canManageTask}
              atribuidoAId={tarefa.atribuidoA?.id}
              criadoPorId={tarefa.criadoPor?.id}
            />
          </div>

          <div className="order-5 min-w-0 lg:order-none">
            <TarefaArquivosCard
              tarefaId={tarefa.id}
              isConcluido={isFechada}
              criadoPorId={tarefa.criadoPor?.id}
              atribuidoAId={tarefa.atribuidoA?.id}
            />
          </div>
        </aside>
      </div>
    </div>
  );
}
